<template>
  <div :class="['ScreenNav', { compact }]">
    <div class="nav-side">
      <slot name="left" />
    </div>

    <NavPill>
      <NavPillButton :label="navLabel('app.nav.me')" :active="active === 'profile'" @select="$emit('profile')">
        <GamIcon :name="GamIconName.PROFILE_EMPTY" :size="iconSize" is-current />
      </NavPillButton>

      <NavPillButton :label="navLabel('app.nav.map')" :active="active === 'map'" @select="$emit('map')">
        <GamIcon :name="GamIconName.MAP" :size="iconSize" is-current />
      </NavPillButton>

      <NavPillButton :label="navLabel('app.nav.meet')" :active="active === 'sparks'" @select="$emit('sparks')">
        <GamIcon :name="GamIconName.PLAY_OUTLINE" :size="iconSize" is-current />
      </NavPillButton>
    </NavPill>

    <div class="nav-side">
      <slot name="right" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import NavPill from '@/views/components/NavPill.vue';
import NavPillButton from '@/views/components/NavPillButton.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { computed } from 'vue';

export type ScreenNavTab = 'profile' | 'map' | 'sparks';

export interface ScreenNavProps {
  active?: ScreenNavTab;
  /* Shrinks the pill and drops the labels, for screens where the nav shares the
     bottom of the viewport with a primary CTA. */
  compact?: boolean;
}

const props = withDefaults(defineProps<ScreenNavProps>(), {
  active: undefined,
  compact: false,
});

const iconSize = computed(() => (props.compact ? GamIconSize.SMALL : GamIconSize.LARGE));

// An absent label drops the row, which is how the compact variant loses its text.
const navLabel = (key: string) => (props.compact ? undefined : localize(key));

defineEmits<{
  profile: [];
  map: [];
  sparks: [];
}>();
</script>

<style lang="scss" scoped>
.ScreenNav {
  position: fixed;
  /* Screens whose content stacks above 500 (e.g. the player detail sections)
     raise this; the map keeps 500 so its overlays still win. */
  z-index: var(--screen-nav-z, 500);
  bottom: var(--safe-bottom-offset);
  left: 0;
  right: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
  pointer-events: none;
}

// Reserves the flanking slot width whether or not a button is rendered, so an
// empty slot still balances a filled one.
.nav-side {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  min-width: var(--nav-cell-size, 3rem);
  height: var(--nav-cell-size, 3rem);
  pointer-events: none;
}

.ScreenNav.disabled {
  opacity: 0.3;
  pointer-events: none;
}

// Shrinks the pills by retuning the tokens they read, which saves reaching into
// the child components' styles.
.ScreenNav.compact {
  --nav-cell-size: 2.25rem;
  --nav-pill-gap: 0.125rem;
  --nav-label-gap: 0rem;

  gap: 0.75rem;
}
</style>
